import {
  Avatar,
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from "@fluentui/react-components";
import {
  TableCell,
  TableCellLayout,
  TableRow,
  TableSelectionCell,
  TableRowData as RowStateBase,
} from "@fluentui/react-components/unstable";
import { MoreHorizontalRegular } from "@fluentui/react-icons";
import { ListChildComponentProps } from "react-window";
import { IUser } from "../../services/types/table";
import { AppConfirmdialog, UserContractTypeDialog, UserManageProjectsDialog } from "../common";
import { useState } from "react";
import { usersService } from "../../services";
import { getContractTypeLabel, parseContractType } from "../../utils/contractType";

interface ReactWindowRenderFnProps extends ListChildComponentProps {
  data: TableRowData[];
}
interface TableRowData extends RowStateBase<IUser> {
  getUsers: () => void;
  onClick: (e: React.MouseEvent) => void;
  selected: boolean;
  appearance: "brand" | "none";
  assigned: boolean;
}

const UserListRow = ({ index, style, data }: ReactWindowRenderFnProps) => {
  const { item, selected, appearance, getUsers, onClick, assigned } = data[index];
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openContractDialog, setOpenContractDialog] = useState(false);
  const [openProjectsDialog, setOpenProjectsDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingContract, setIsUpdatingContract] = useState(false);
  const [contractType, setContractType] = useState<number | null>(() =>
    parseContractType(item.contractType)
  );
  const [serviceEndDate, setServiceEndDate] = useState<string | null>(item.serviceEndDate ?? null);
  const [hasDelayedServiceAcceptance, setHasDelayedServiceAcceptance] = useState(
    item.hasDelayedServiceAcceptance ?? false
  );

  const deleteUser = () => {
    setIsDeleting(true);
    usersService.deleteUser(item.id)
      .then(() => {
        setIsDeleting(false);
        setOpenDeleteDialog(false);
        getUsers();
      });
  };

  const updateContractType = () => {
    if (contractType === null) {
      return;
    }

    setIsUpdatingContract(true);
    usersService.updateUserContractType(
      item.id,
      contractType,
      serviceEndDate ? serviceEndDate.slice(0, 10) : null,
      hasDelayedServiceAcceptance
    )
      .then(() => {
        setOpenContractDialog(false);
        getUsers();
      })
      .finally(() => {
        setIsUpdatingContract(false);
      });
  };

  const openContractEditor = () => {
    setContractType(parseContractType(item.contractType));
    setServiceEndDate(item.serviceEndDate ?? null);
    setHasDelayedServiceAcceptance(item.hasDelayedServiceAcceptance ?? false);
    setOpenContractDialog(true);
  };

  const contractTypeLabel = getContractTypeLabel(item.contractType);

  return (
    <TableRow
      aria-rowindex={index + 2}
      style={style}
      onClick={onClick}
      appearance={appearance}
    >
      {!assigned && (
        <TableSelectionCell
          checked={selected}
          checkboxIndicator={{
            "aria-label": "Select row",
          }}
        />
      )}
      <TableCell>
        <TableCellLayout
          media={
            <Avatar
              aria-label={`${item.lastName} ${item.firstName}`}
              name={`${item.lastName} ${item.firstName}`}
            />
          }
        >
          {`${item.lastName} ${item.firstName}`}
        </TableCellLayout>
      </TableCell>
      {assigned && (
        <TableCell>
          <TableCellLayout>{contractTypeLabel}</TableCellLayout>
        </TableCell>
      )}
      {assigned && (
        <TableCell>
          <TableCellLayout>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button
                  size="small"
                  appearance="outline"
                  icon={<MoreHorizontalRegular />}
                  aria-label="Akcje pracownika"
                  onClick={(e) => e.stopPropagation()}
                />
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      openContractEditor();
                    }}
                  >
                    Zmień umowę
                  </MenuItem>
                  <MenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenProjectsDialog(true);
                    }}
                  >
                    Zarządzaj projektami
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          </TableCellLayout>
        </TableCell>
      )}
      {assigned &&
        <TableCell>
          <TableCellLayout>
            <Button onClick={(e) => {
              e.stopPropagation();
              setOpenDeleteDialog(true);
            }}>Usuń pracownika</Button>
          </TableCellLayout>
        </TableCell>
      }
      <AppConfirmdialog
        title='Potwierdź usunięcie pracownika'
        bodyTxt='Czy na pewno chcesz usunąć tego pracownika?'
        setOpen={setOpenDeleteDialog}
        open={openDeleteDialog}
        onAccept={deleteUser}
        isLoading={isDeleting}
      />
      <UserContractTypeDialog
        open={openContractDialog}
        setOpen={setOpenContractDialog}
        contractType={contractType}
        onContractTypeChange={setContractType}
        serviceEndDate={serviceEndDate}
        onServiceEndDateChange={setServiceEndDate}
        hasDelayedServiceAcceptance={hasDelayedServiceAcceptance}
        onHasDelayedServiceAcceptanceChange={setHasDelayedServiceAcceptance}
        clearanceSettlementState={item.clearanceSettlementState ?? "notApplicable"}
        delayedAcceptanceSettlementState={item.delayedAcceptanceSettlementState ?? "notApplicable"}
        clearanceSettledAt={item.clearanceSettledAt ?? null}
        clearanceSettledByName={item.clearanceSettledByName ?? null}
        delayedAcceptanceSettledAt={item.delayedAcceptanceSettledAt ?? null}
        delayedAcceptanceSettledByName={item.delayedAcceptanceSettledByName ?? null}
        onSave={updateContractType}
        isLoading={isUpdatingContract}
      />
      <UserManageProjectsDialog
        userId={item.id}
        open={openProjectsDialog}
        setOpen={setOpenProjectsDialog}
      />
    </TableRow>
  );
};

export default UserListRow;
